import { useCallback, useRef } from "react";
import { Drawer } from "@design-system/Drawer";
import {
    BreadcrumbItem,
    Breadcrumbs,
} from "@design-system/Breadcrumbs/Breadcrumbs";
import invoiceStyles from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceDrawer.module.scss";
import { LayoutEditor } from "./layout/LayoutEditor";
import { TemplateLayoutEditor } from "./layout/TemplateLayoutEditor";
import {
    REPORT_SETTINGS_DRAWER_TITLE,
    REPORT_SETTINGS_TEMPLATE_DRAWER_TITLE,
    ReportSettingsEditorSubject,
} from "./reportSettingsEditor";
import styles from "./ReportSettings.module.scss";

/** Attached by an editor body that guards unsaved edits; the drawer routes every close request through it. */
export interface ReportSettingsEditorHandle {
    requestClose: () => void;
}

interface Props {
    open: boolean;
    subject: ReportSettingsEditorSubject;
    onClose: () => void;
}

export const ReportSettingsEditorDrawer = ({
    open,
    subject,
    onClose,
}: Props) => {
    const contentRef = useRef<ReportSettingsEditorHandle>(null);

    const handleClose = useCallback(() => {
        if (contentRef.current) {
            contentRef.current.requestClose();
            return;
        }
        onClose();
    }, [onClose]);

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            headerClassName={invoiceStyles.flatHeader}
            direction="right"
            size="fullScreen"
            noPadding
            headerAddonLeft={
                <Breadcrumbs
                    aria-label="Report settings"
                    className={invoiceStyles.draftHeaderBreadcrumbs}
                >
                    {subject.kind === "template" ? (
                        <>
                            <BreadcrumbItem>
                                {REPORT_SETTINGS_TEMPLATE_DRAWER_TITLE}
                            </BreadcrumbItem>
                            <BreadcrumbItem current>
                                {subject.templateName}
                            </BreadcrumbItem>
                        </>
                    ) : (
                        <BreadcrumbItem current>
                            {REPORT_SETTINGS_DRAWER_TITLE}
                        </BreadcrumbItem>
                    )}
                </Breadcrumbs>
            }
        >
            <div className={styles.editorMount}>
                {subject.kind === "template" ? (
                    <TemplateLayoutEditor
                        ref={contentRef}
                        templateId={subject.templateId}
                        isOpen={open}
                        onRequestClose={onClose}
                    />
                ) : (
                    <LayoutEditor
                        ref={contentRef}
                        workspaceId={subject.workspaceId}
                        initialEntityId={subject.entityId}
                        isOpen={open}
                        onRequestClose={onClose}
                    />
                )}
            </div>
        </Drawer>
    );
};
